import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCents, formatDate } from "@/lib/api";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Select, Modal, Textarea } from "@/components/ui/Primitives";
import { ClipboardList, Filter, Sparkles, TrendingUp, AlertTriangle, Loader2, Building2, Share2, Check, X } from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const STATUSES = ["new", "in_review", "screening_ordered", "approved", "rejected", "withdrawn"];
const TONE = {
  new: "blue", in_review: "amber", screening_ordered: "violet",
  approved: "green", rejected: "red", withdrawn: "slate"
};

export default function Applications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [updating, setUpdating] = useState(false);
  const [rentCents, setRentCents] = useState("");
  const [termMonths, setTermMonths] = useState(12);

  const load = async () => {
    setLoading(true);
    try {
      const url = filter ? `/applications?status=${filter}` : "/applications";
      const { data } = await api.get(url);
      setItems(Array.isArray(data) ? data : (data?.applications || []));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const approve = async (app) => {
    setUpdating(true);
    try {
      const body = { term_months: Number(termMonths) || 12 };
      if (rentCents) body.monthly_rent_cents = Number(rentCents);
      const { data } = await api.post(`/applications/${app.application_id}/approve`, body);
      toast.success(data.already_approved
        ? "Already approved — lease is on file."
        : "Approved. Lease created, tenant notified.");
      setSelected(null);
      setNote("");
      setRentCents("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not approve");
    } finally { setUpdating(false); }
  };

  const decline = async (app) => {
    setUpdating(true);
    try {
      await api.post(`/applications/${app.application_id}/decline`, { reason: note || null });
      toast.success("Declined. Applicant emailed.");
      setSelected(null);
      setNote("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not decline");
    } finally { setUpdating(false); }
  };

  if (loading) return <Loader/>;

  return (
    <div>
      <PageHeader
        title="Applications"
        subtitle="Incoming tenant applications from public listings."
        actions={
          <Select value={filter} onChange={(e) => setFilter(e.target.value)} className="w-44" testid="apps-filter-select">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_"," ")}</option>)}
          </Select>
        }
      />

      {items.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-10" data-testid="applications-empty">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto">
              <ClipboardList className="w-6 h-6 text-slate-500"/>
            </div>
            <h3 className="mt-4 font-display text-xl font-bold text-slate-900">No applications yet</h3>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">
              Applications land here the moment a tenant applies from one of your public
              listing pages. Two quick ways to start receiving them:
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3 text-left">
              <Link to="/properties" data-testid="applications-empty-cta-listings"
                className="group rounded-xl border border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50 p-4 transition">
                <div className="flex items-center gap-2 text-emerald-800 font-bold text-sm">
                  <Building2 className="w-4 h-4"/> Publish a listing
                </div>
                <p className="mt-1 text-xs text-emerald-900/70">Create or reactivate a listing so it&apos;s discoverable and can accept applications.</p>
              </Link>
              <Link to="/community?tab=listings" data-testid="applications-empty-cta-share"
                className="group rounded-xl border border-sky-200 bg-sky-50/60 hover:bg-sky-50 p-4 transition">
                <div className="flex items-center gap-2 text-sky-800 font-bold text-sm">
                  <Share2 className="w-4 h-4"/> Share to the community
                </div>
                <p className="mt-1 text-xs text-sky-900/70">Post your listing to the tenant community feed — 3–5× more applications.</p>
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {items.map((a) => (
            <ApplicantCard
              key={a.application_id}
              a={a}
              onOpen={() => setSelected(a)}
              onQuickApprove={(e) => {
                e.stopPropagation();
                setSelected(a);
              }}
              onQuickDecline={async (e) => {
                e.stopPropagation();
                if (!window.confirm(`Decline ${a.full_name}? They'll be notified by email.`)) return;
                try {
                  await api.post(`/applications/${a.application_id}/decline`, { reason: null });
                  toast.success("Declined. Applicant emailed.");
                  load();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Could not decline");
                }
              }}
            />
          ))}
        </div>
      )}

      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected?.full_name || ""} testid="application-modal">
        {selected && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info label="Email">{selected.email}</Info>
              <Info label="Phone">{selected.phone || "—"}</Info>
              <Info label="Move-in">{selected.move_in_date || "—"}</Info>
              <Info label="Monthly income">{selected.monthly_income_cents ? formatCents(selected.monthly_income_cents) : "—"}</Info>
              <Info label="Employment">{selected.employment_status || selected.employment?.status || "—"}</Info>
              <Info label="Pets">{selected.pets || "—"}</Info>
            </div>
            {selected.notes && (
              <div className="text-sm">
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Notes</div>
                <div className="bg-slate-50 rounded-md p-3 border border-slate-100 text-slate-700">{selected.notes}</div>
              </div>
            )}
            <MatchScoreBlock applicationId={selected.application_id}/>
            <div className="border-t border-slate-100 pt-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">Monthly rent (¢)</div>
                  <input type="number" value={rentCents} onChange={(e) => setRentCents(e.target.value)}
                    placeholder="e.g. 250000 = $2,500"
                    data-testid="approve-rent-input"
                    className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">Term (months)</div>
                  <input type="number" value={termMonths} onChange={(e) => setTermMonths(e.target.value)}
                    data-testid="approve-term-input"
                    className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm" />
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Decision note (used on decline)</div>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for decline…" testid="application-note-input"/>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="primary" disabled={updating} onClick={() => approve(selected)} data-testid="application-approve-button">
                  {updating ? "Approving…" : "Approve & create lease"}
                </Button>
                <Button variant="danger" disabled={updating} onClick={() => decline(selected)} data-testid="application-reject-button">
                  Decline
                </Button>
              </div>
              <div className="text-[11px] text-slate-500">
                Approving creates a fixed-term lease on the first vacant unit of the property, seeds 12 months of rent dues, and emails the tenant.
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
/**
 * ApplicantCard — modernised applicant row. Adds:
 *   • Initials avatar (deterministic gradient by name hash).
 *   • Client-side match-score gauge (income/rent ratio + move-in freshness
 *     + occupants headroom). Runs zero-network so the card renders
 *     instantly even before the applicant clicks in to see the full detail.
 *   • Hover quick-actions (Approve / Decline) — pill buttons that appear
 *     on md+ hover, always visible on touch.
 */
function ApplicantCard({ a, onOpen, onQuickApprove, onQuickDecline }) {
  const initials = (a.full_name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("") || "?";

  const hash = Array.from(a.full_name || "").reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 7);
  const grads = [
    "from-emerald-500 to-teal-600",
    "from-sky-500 to-indigo-600",
    "from-fuchsia-500 to-rose-600",
    "from-amber-500 to-orange-600",
    "from-violet-500 to-purple-600",
  ];
  const grad = grads[Math.abs(hash) % grads.length];

  // Fast, transparent match-score. Every signal shown to the user; no
  // hidden model, no fabrication. Missing signals are excluded.
  const signals = [];
  let score = 50;
  const monthlyIncome = a.monthly_income_cents ? a.monthly_income_cents / 100 : 0;
  const desiredRent = a.desired_rent_cents ? a.desired_rent_cents / 100 : 0;
  if (monthlyIncome && desiredRent) {
    const ratio = monthlyIncome / desiredRent;
    if (ratio >= 3) { score += 25; signals.push("3× income"); }
    else if (ratio >= 2.5) { score += 15; signals.push("2.5× income"); }
    else if (ratio >= 2) { score += 5; signals.push("2× income"); }
    else { score -= 10; signals.push("Under 2× income"); }
  }
  if (a.move_in_date) {
    const days = Math.max(0, Math.ceil((new Date(a.move_in_date) - Date.now()) / 86400000));
    if (days <= 30) { score += 10; signals.push(`Move-in ${days}d`); }
    else if (days <= 60) { score += 5; signals.push(`Move-in ${days}d`); }
  }
  if (a.occupants && Number(a.occupants) <= 3) { score += 5; signals.push("≤3 occupants"); }
  if (a.credit_verified) { score += 10; signals.push("Credit verified"); }
  score = Math.min(100, Math.max(0, score));

  const scoreTone = score >= 75 ? "emerald" : score >= 55 ? "amber" : "rose";
  const scoreRing = { emerald: "text-emerald-600 ring-emerald-200", amber: "text-amber-600 ring-amber-200", rose: "text-rose-600 ring-rose-200" }[scoreTone];
  const scoreLabel = score >= 75 ? "Top match" : score >= 55 ? "Solid" : "Review carefully";
  const isTerminal = a.status === "rejected" || a.status === "approved";

  return (
    <Card
      className="p-5 card-lift cursor-pointer group relative"
      onClick={onOpen}
      data-testid={`application-card-${a.application_id}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${grad} text-white font-display font-extrabold text-base flex items-center justify-center shrink-0 shadow-sm`} data-testid={`applicant-avatar-${a.application_id}`}>
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-lg font-bold text-slate-900 truncate">{a.full_name}</h3>
            <Pill tone={TONE[a.status] || "slate"}>{(a.status || "").replace("_", " ")}</Pill>
          </div>
          <div className="text-sm text-slate-500 truncate">{a.email}</div>
          {a.phone && <div className="text-xs text-slate-500 mt-0.5">{a.phone}</div>}
        </div>
        <div className={`shrink-0 flex flex-col items-center rounded-full ring-2 ${scoreRing} bg-white w-14 h-14 justify-center`} data-testid={`applicant-score-${a.application_id}`} title={scoreLabel}>
          <div className="font-display font-extrabold text-lg leading-none tabular-nums">{score}</div>
        </div>
      </div>

      {signals.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`applicant-signals-${a.application_id}`}>
          {signals.slice(0, 4).map((s, i) => (
            <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{s}</span>
          ))}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Move-in</div>
          <div className="font-semibold text-slate-900">{a.move_in_date || "—"}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Income/mo</div>
          <div className="font-semibold text-slate-900">{a.monthly_income_cents ? formatCents(a.monthly_income_cents) : "—"}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Occupants</div>
          <div className="font-semibold text-slate-900">{a.occupants || "—"}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Submitted</div>
          <div className="font-semibold text-slate-900">{formatDate(a.created_at)}</div>
        </div>
      </div>

      {!isTerminal && (
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2 md:opacity-0 md:pointer-events-none md:group-hover:opacity-100 md:group-hover:pointer-events-auto transition-opacity">
          <button
            type="button"
            onClick={onQuickDecline}
            data-testid={`applicant-decline-${a.application_id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-rose-200 bg-white text-rose-700 text-xs font-bold hover:bg-rose-50"
          >
            <X className="w-3.5 h-3.5" /> Decline
          </button>
          <button
            type="button"
            onClick={onQuickApprove}
            data-testid={`applicant-approve-${a.application_id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700"
          >
            <Check className="w-3.5 h-3.5" /> Open · Approve
          </button>
        </div>
      )}
    </Card>
  );
}



function Info({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className="mt-0.5 text-slate-900 font-medium break-words">{children}</div>
    </div>
  );
}

/**
 * Calls Smart Mark to score the applicant vs the listing they applied
 * to. Cached server-side so flipping back to the modal is instant.
 */
function MatchScoreBlock({ applicationId }) {
  const [score, setScore] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    if (!applicationId) return;
    setBusy(true); setError("");
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("crc_token") || "";
      const r = await fetch(`${API}/api/match-score/application/${applicationId}`, {
        headers: token ? { "Authorization": `Bearer ${token}` } : {},
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not compute");
      setScore(d);
    } catch (e) { setError(String(e.message || e)); }
    finally { setBusy(false); }
  }

  const tone = score ? (score.score >= 75 ? "green" : score.score >= 50 ? "amber" : "rose") : "slate";
  const TONE = {
    green: "bg-green-50 border-green-300 text-green-900",
    amber: "bg-amber-50 border-amber-300 text-amber-900",
    rose:  "bg-rose-50 border-rose-300 text-rose-900",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  }[tone];
  const PILL = {
    green: "bg-green-700",
    amber: "bg-amber-700",
    rose:  "bg-rose-700",
    slate: "bg-slate-700",
  }[tone];

  return (
    <div className="border-t border-slate-100 pt-5" data-testid="match-score-block">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-emerald-700"/> Smart Mark · Match Score
        </div>
        {!score && (
          <Button type="button" variant="secondary" className="text-xs py-1 px-2.5" onClick={load} disabled={busy} data-testid="compute-match-score">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Compute"}
          </Button>
        )}
      </div>
      {error && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded">{error}</div>}
      {score && (
        <div className={`rounded-lg border p-4 ${TONE}`}>
          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 w-16 h-16 rounded-full text-white flex items-center justify-center ${PILL}`}>
              <div className="text-center leading-tight">
                <div className="font-display text-2xl font-extrabold" data-testid="match-score-value">{score.score}</div>
                <div className="text-[9px] uppercase tracking-widest opacity-80">/100</div>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm font-bold">{score.headline}</div>
              <p className="text-xs mt-1 opacity-90">{score.reasoning}</p>
            </div>
          </div>
          {(score.strengths?.length || 0) > 0 && (
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold opacity-80 mb-1 flex items-center gap-1"><TrendingUp className="w-3 h-3"/> Strengths</div>
                <ul className="space-y-0.5">{score.strengths.map((s, i) => <li key={i} className="text-xs">+ {s}</li>)}</ul>
              </div>
              {(score.concerns?.length || 0) > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold opacity-80 mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3"/> Concerns</div>
                  <ul className="space-y-0.5">{score.concerns.map((s, i) => <li key={i} className="text-xs">– {s}</li>)}</ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
